/**
 * Hook for fetching user's trip history with pagination
 */

import { useState, useEffect, useCallback } from 'react';
import * as api from '../lib/api';
import type { HistoryKitItem } from '../lib/api';

const SESSION_TOKEN_KEY = 'nfa_session_token';

interface UseHistoryResult {
  kits: HistoryKitItem[];
  total: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

const ITEMS_PER_PAGE = 10;

export function useHistory(): UseHistoryResult {
  const [kits, setKits] = useState<HistoryKitItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async (reset = false) => {
    const token = getSessionToken();
    if (!token) {
      setKits([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const newOffset = reset ? 0 : offset;
      const response = await api.fetchHistory(token, {
        limit: ITEMS_PER_PAGE,
        offset: newOffset,
      });

      if (reset) {
        setKits(response.kits);
        setOffset(response.kits.length);
      } else {
        setKits(prev => [...prev, ...response.kits]);
        setOffset(prev => prev + response.kits.length);
      }
      setTotal(response.total);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        // Not authenticated - clear items
        setKits([]);
        setTotal(0);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load history');
      }
    } finally {
      setIsLoading(false);
    }
  }, [offset]);

  // Initial fetch
  useEffect(() => {
    fetchHistory(true);
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(async () => {
    const token = getSessionToken();
    if (!token || isLoading) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.fetchHistory(token, {
        limit: ITEMS_PER_PAGE,
        offset,
      });

      setKits(prev => [...prev, ...response.kits]);
      setOffset(prev => prev + response.kits.length);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more history');
    } finally {
      setIsLoading(false);
    }
  }, [offset, isLoading]);

  const refresh = useCallback(async () => {
    setOffset(0);
    await fetchHistory(true);
  }, [fetchHistory]);

  const hasMore = kits.length < total;

  return {
    kits,
    total,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}
